import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  CheckCircle2,
  User,
  Package,
  Building2,
  MapPin,
  ChevronRight,
  X,
  BadgeCheck,
  Info,
  Send,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import type { UniversalScanResult } from "../services/scanService";
import { getScanTargetLabel } from "../services/codeRecognitionService";
import { Button } from "./Button";
import { QuickAnnotatePanel } from "./QuickAnnotatePanel";
import { useAppStore } from "../store/useAppStore";
import { useTranslation } from "react-i18next";
import { adminAuthStorage } from "../api/authStorage";
import { leadsService } from "../services/leadsService";
import { scanApi } from "../api/scan";
import {
  CONTACT_MARK_PRESETS,
  CONTACT_MARK_TONE_CLASS,
  markLabel,
} from "../utils/contactMarks";
import {
  newAnnotationNote,
  parseAnnotationNotes,
  serializeAnnotationNotes,
  type AnnotationNote,
} from "../utils/annotationNotes";

type EnrichedScanResult = UniversalScanResult["scanResult"];

interface ScanResultModalProps {
  scanResult: EnrichedScanResult;
  alreadyScanned: boolean;
  leadId?: string;
  eventId?: string;
  /** Persisted scan id — enables server-side notes for visitors. */
  scanId?: string;
  initialNotes?: string;
  initialTags?: string[];
  onClose: () => void;
}

export function ScanResultModal({
  scanResult,
  alreadyScanned,
  leadId,
  eventId,
  scanId,
  initialNotes = "",
  initialTags = [],
  onClose,
}: ScanResultModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setNavbarVisible, savedContacts, updateSavedContact, updateScan, user, selectedEventId } =
    useAppStore();

  const companyId =
    scanResult.company?.id ??
    scanResult.product?.companyId ??
    scanResult.representative?.companyId;

  const existingContact = useMemo(
    () => (companyId ? savedContacts.find((c) => c.companyId === companyId) : undefined),
    [companyId, savedContacts],
  );

  const isRepresentative = scanResult.targetType === "representative";
  const isCompanyOrBooth = scanResult.targetType === "company" || scanResult.targetType === "booth";
  const isProduct = scanResult.targetType === "product";
  const isVisitor = scanResult.targetType === "visitor";
  const sharedBoothCompanies = scanResult.companies ?? [];
  const isSharedBooth = sharedBoothCompanies.length > 1;

  // Seed cards from this scan only (never company favorites — that leaked across colleagues).
  const seedNotesRaw =
    isRepresentative || isProduct || isVisitor
      ? initialNotes || ""
      : initialNotes || existingContact?.notes || "";
  const [noteEntries, setNoteEntries] = useState<AnnotationNote[]>(() =>
    parseAnnotationNotes(seedNotesRaw, initialTags),
  );

  useEffect(() => {
    setNavbarVisible(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      setNavbarVisible(true);
      document.body.style.overflow = prev;
    };
  }, [setNavbarVisible]);

  // Visitor→participant/product: always allow notes when we have a scan or company.
  // Exhibitor→visitor: notes via lead CRM and/or scan row.
  // Shared stand: annotate after picking a company (no single companyId yet).
  const canAnnotate =
    !isSharedBooth &&
    (Boolean(scanId) || Boolean(companyId) || (isVisitor && Boolean(leadId)));

  const handleViewDetails = () => {
    let path: string | null = null;
    if (isSharedBooth) {
      const booth = scanResult.booth || sharedBoothCompanies[0]?.booth;
      const eid = eventId ?? selectedEventId;
      if (booth && eid) {
        path = `/booths/${encodeURIComponent(eid)}/${encodeURIComponent(booth)}`;
      }
    } else if (isVisitor) {
      if (leadId) {
        path = `/exhibitor/leads/${leadId}`;
      } else if (scanResult.visitor?.id) {
        path = `/visitors/${scanResult.visitor.id}`;
      } else {
        path = "/history";
      }
    } else {
      switch (scanResult.targetType) {
        case "representative":
          if (scanResult.representative) path = `/representatives/${scanResult.representative.id}`;
          break;
        case "product":
          if (scanResult.product) path = `/products/${scanResult.product.id}`;
          break;
        case "company":
        case "booth":
          if (scanResult.company) path = `/companies/${scanResult.company.id}`;
          break;
        default:
          break;
      }
    }
    if (path) navigate(path);
    onClose();
  };

  const handleAnnotate = async (values: { tags: string[]; notes: string }) => {
    const eid = eventId ?? selectedEventId;
    const errors: string[] = [];
    let persisted = false;

    // Append onto live cards (not just initialNotes) so a 2nd save in this modal keeps the 1st.
    const nextNotes = [...noteEntries, newAnnotationNote(values.notes, values.tags)];
    const nextNotesPayload = serializeAnnotationNotes(nextNotes);
    const nextTagsPayload = [...new Set(nextNotes.flatMap((n) => n.tags))];

    // Company favorites notes are for company/booth scans only.
    // Rep/product notes stay on the scan row — writing them onto the company
    // contact leaked "previous colleague" text into the next scan composer.
    if (companyId && isCompanyOrBooth) {
      updateSavedContact(companyId, {
        notes: nextNotesPayload,
        tags: nextTagsPayload,
        eventId: eid ?? "",
        userId: user.id,
      });
      persisted = true;
    }

    if (scanId && eid) {
      try {
        await scanApi.updateNotes(eid, scanId, {
          notes: nextNotesPayload,
          tags: nextTagsPayload,
        });
        updateScan(scanId, { notes: nextNotesPayload, tags: nextTagsPayload });
        persisted = true;
      } catch (err) {
        errors.push(err instanceof Error ? err.message : "scan_notes");
      }
    }

    // CRM lead notes only for exhibitor→visitor (or admin portal).
    if (leadId && eid && (isVisitor || adminAuthStorage.isAuthenticated())) {
      try {
        const audience = adminAuthStorage.isAuthenticated() ? "admin" : "visitor";
        await leadsService.update(
          eid,
          leadId,
          { notes: nextNotesPayload, tags: nextTagsPayload },
          audience,
        );
        persisted = true;
      } catch (err) {
        errors.push(err instanceof Error ? err.message : "lead_notes");
      }
    }

    // Local-only fallback: company/booth without a scan row yet.
    if (!scanId && !leadId && companyId && isCompanyOrBooth) {
      persisted = true;
    }

    if (persisted && errors.length === 0) {
      setNoteEntries(nextNotes);
      toast.success(t("annotate.saved"));
      return;
    }
    toast.error(t("annotate.save_failed"));
    throw new Error(errors[0] || "annotate_failed");
  };

  const annotatePresets: ReadonlyArray<{
    id: string;
    label: string;
    tone: "amber" | "rose" | "orange" | "sky" | "slate";
  }> = isProduct
    ? [
        { id: "important", label: t("marks.product_new"), tone: "amber" },
        { id: "urgent", label: t("marks.product_unique"), tone: "rose" },
        { id: "hot", label: t("marks.product_ru"), tone: "orange" },
        { id: "ski", label: t("marks.product_study"), tone: "slate" },
        { id: "followup", label: t("marks.followup"), tone: "sky" },
      ]
    : CONTACT_MARK_PRESETS.map((p) => ({
        id: p.id,
        label: markLabel(p.id, t),
        tone: p.tone,
      }));

  const getIcon = () => {
    switch (scanResult.targetType) {
      case "representative":
      case "badge":
        return User;
      case "product":
        return Package;
      case "company":
      case "booth":
        return Building2;
      case "map_point":
        return MapPin;
      case "visitor":
        return User;
      default:
        return Building2;
    }
  };

  const Icon = getIcon();

  const getTitle = () => {
    switch (scanResult.targetType) {
      case "representative":
        return scanResult.representative?.fullName || t("visitor.representative");
      case "product":
        return scanResult.product?.name || t("visitor.product");
      case "company":
        return scanResult.company?.name || t("companies.detail_title");
      case "booth":
        if (isSharedBooth) {
          return t("location.booth", {
            value: scanResult.booth || sharedBoothCompanies[0]?.booth || "",
          });
        }
        return scanResult.company?.name || t("location.booth", { value: scanResult.company?.booth });
      case "visitor":
        return scanResult.visitor?.fullName || t("visitor.visitor");
      default:
        return t("visitor.scanned_object");
    }
  };

  const getSubtitle = () => {
    switch (scanResult.targetType) {
      case "representative":
        return scanResult.representative?.position;
      case "product":
        return scanResult.product?.category;
      case "company":
      case "booth":
        if (isSharedBooth) {
          return t("booth_slot.subtitle", { n: sharedBoothCompanies.length });
        }
        return scanResult.company?.category;
      case "visitor":
        return [scanResult.visitor?.companyName, scanResult.visitor?.position]
          .filter(Boolean)
          .join(" · ") || scanResult.visitor?.phone;
      default:
        return undefined;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg max-h-[min(92dvh,100%)] overflow-y-auto overscroll-y-contain"
        >
          <div className="relative rounded-2xl bg-white border border-gray-100 shadow-xl p-5">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>

            <div className="flex flex-col items-center text-center mb-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
                className={`w-14 h-14 rounded-full flex items-center justify-center mb-2 ${
                  isRepresentative ? "bg-indigo-100" : "bg-emerald-100"
                }`}
              >
                {isRepresentative ? (
                  <BadgeCheck size={28} className="text-indigo-600" />
                ) : (
                  <CheckCircle2 size={28} className="text-emerald-600" />
                )}
              </motion.div>

              <h2 className="text-[17px] text-gray-900 mb-1">
                {alreadyScanned ? t("visitor.scan_already") : t("visitor.scan_success")}
              </h2>

              <span
                className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1 rounded-full ${
                  isRepresentative
                    ? "bg-indigo-50 text-indigo-600"
                    : isCompanyOrBooth
                      ? "bg-emerald-50 text-emerald-600"
                      : "bg-gray-100 text-gray-500"
                }`}
              >
                <Icon size={12} />
                {isRepresentative
                  ? t("visitor.representative_card")
                  : isCompanyOrBooth
                    ? t("visitor.company_info")
                    : getScanTargetLabel(scanResult.targetType, t)}
              </span>
            </div>

            <div className="mb-3">
              <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-gray-50">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isRepresentative
                      ? "bg-gradient-to-br from-indigo-100 to-purple-100"
                      : "bg-gradient-to-br from-emerald-100 to-teal-100"
                  }`}
                >
                  <Icon size={22} className={isRepresentative ? "text-indigo-600" : "text-emerald-600"} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] text-gray-900 mb-0.5">{getTitle()}</h3>
                  {getSubtitle() ? (
                    <p className="text-[13px] text-gray-500">{getSubtitle()}</p>
                  ) : null}
                  {isVisitor && scanResult.visitor?.activityType ? (
                    <p className="text-[12px] text-gray-600 mt-1.5">
                      <span className="text-gray-400">{t("visitor.activity_type")}: </span>
                      {scanResult.visitor.activityType}
                    </p>
                  ) : null}
                  {isVisitor && scanResult.visitor?.interestedProducts ? (
                    <p className="text-[12px] text-gray-600 mt-0.5">
                      <span className="text-gray-400">{t("visitor.interested_products")}: </span>
                      {scanResult.visitor.interestedProducts}
                    </p>
                  ) : null}
                  {isVisitor &&
                  (scanResult.visitor?.phone || scanResult.visitor?.email) ? (
                    <div className="mt-1.5 space-y-0.5 text-[12px] text-indigo-600">
                      {scanResult.visitor.phone ? <p>{scanResult.visitor.phone}</p> : null}
                      {scanResult.visitor.email ? <p>{scanResult.visitor.email}</p> : null}
                    </div>
                  ) : null}
                  {scanResult.company && isRepresentative ? (
                    <div className="flex items-center gap-1 text-[12px] text-gray-400 mt-1">
                      <Building2 size={11} />
                      <span>{scanResult.company.name}</span>
                    </div>
                  ) : null}
                  {isCompanyOrBooth && scanResult.company?.booth ? (
                    <div className="flex items-center gap-1 text-[12px] text-gray-400 mt-1">
                      <MapPin size={11} />
                      <span>{t("location.booth", { value: scanResult.company.booth })}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {isSharedBooth ? (
              <div className="mb-4 space-y-2 max-h-56 overflow-y-auto">
                <p className="text-[12px] text-gray-500 px-0.5">{t("booth_slot.pick_company")}</p>
                {sharedBoothCompanies.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      navigate(`/companies/${c.id}`);
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 active:bg-indigo-50 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] text-gray-900 truncate">{c.name}</p>
                      {c.category ? (
                        <p className="text-[12px] text-gray-500 truncate">{c.category}</p>
                      ) : null}
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : canAnnotate ? (
              <div className="mb-4 p-3 rounded-2xl border border-indigo-100 bg-indigo-50/40 space-y-3">
                {noteEntries.length > 0 ? (
                  <div className="space-y-2">
                    {noteEntries.map((note) => (
                      <div
                        key={note.id}
                        className="rounded-xl border border-indigo-100 bg-white/80 px-3 py-2.5"
                      >
                        {note.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {note.tags.map((tagId) => {
                              const preset = CONTACT_MARK_PRESETS.find((p) => p.id === tagId);
                              const tone = preset
                                ? CONTACT_MARK_TONE_CLASS[preset.tone]
                                : CONTACT_MARK_TONE_CLASS.slate;
                              return (
                                <span
                                  key={tagId}
                                  className={`px-2 py-0.5 rounded-full border text-[11px] ${tone.on}`}
                                >
                                  {markLabel(tagId, t)}
                                </span>
                              );
                            })}
                          </div>
                        ) : null}
                        {note.text ? (
                          <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{note.text}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <QuickAnnotatePanel
                  compact
                  // Composer is for a NEW note only — never prefill with prior
                  // scan/company JSON (leaked across colleagues of the same firm).
                  initialNotes=""
                  initialTags={[]}
                  title={t("annotate.title")}
                  placeholder={
                    isProduct ? t("annotate.placeholder_product") : t("annotate.placeholder")
                  }
                  saveLabel={t("annotate.save")}
                  presets={annotatePresets}
                  onSave={handleAnnotate}
                />
              </div>
            ) : isRepresentative ? (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-indigo-50/70">
                <Send size={13} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                <p className="text-[12px] text-indigo-600 leading-snug">{t("visitor.scan_rep_hint")}</p>
              </div>
            ) : isCompanyOrBooth ? (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-gray-50">
                <Info size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-[12px] text-gray-500 leading-snug">{t("visitor.scan_company_hint")}</p>
              </div>
            ) : null}

            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} className="flex-1">
                {t("common.close")}
              </Button>
              <Button variant="primary" onClick={handleViewDetails} className="flex-1">
                <span>
                  {isVisitor
                    ? leadId
                      ? t("visitor.open_lead")
                      : t("visitor.open_card")
                    : isSharedBooth
                      ? t("booth_slot.open_list")
                      : isRepresentative
                        ? t("visitor.open_card")
                        : t("scan.details")}
                </span>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
